import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import ScrollToTop from "react-scroll-to-top";
import RouteScrollToTop from "./helper/RouteScrollToTop";
import { Toaster } from "react-hot-toast";
import AutoLogoutManager from "./components/AutoLogoutManager";
import PortalSelectionModal from "./components/Modals/PortalSelectionModal";
import { isTokenExpired } from "./utils/authSession";

/* ---------------- Loader ---------------- */
const Loader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
  </div>
);

/* ---------------- Public Pages ---------------- */
const HomePage = lazy(() => import("./pages/HomePage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const CentreRegisterPage = lazy(() => import("./pages/CentreRegisterPage"));
const PatientRegisterPage = lazy(() => import("./pages/PatientRegisterPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const AllFaq = lazy(() => import("./pages/AllFaq"));


const AllCenterPage = lazy(() => import("./pages/AllCentersPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const HealthPackages = lazy(() => import("./pages/HealthPackages"));

const ProductDetailsPage = lazy(() => import("./pages/ProductDetailsPage"));
const PackageDetsilsPage = lazy(() => import("./pages/PackageDetsilsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

const CartPage = lazy(() => import("./pages/CartPage"));
const CartPersonalPage = lazy(() => import("./pages/CartPersonalPage"));
const CartThankYouPage = lazy(() => import("./pages/CartThankYouPage"));

const PharmacyPage = lazy(() => import("./pages/PharmacyPage"));
const MedicalInstrumentsPage = lazy(
  () => import("./pages/MedicalInstrumentsPage"),
);
const HealthProductPage = lazy(() => import("./pages/HealthProductPage"));

const TermsBooking = lazy(() => import("./components/TermsBooking"));
const UserGuide = lazy(() => import("./components/UserGuide"));
const PrivacyBooking = lazy(() => import("./components/PrivacyBooking"));
const PrivacyOrders = lazy(() => import("./components/PrivacyOrders"));
const PaymentPolicyCentre = lazy(
  () => import("./components/PaymentPolicyCentre"),
);
const ReviewLogin = lazy(() => import("./components/ReviewLogin"));
const UserLogin = lazy(() => import("./components/UserLogin"));
const LoginCentre = lazy(() => import("./components/LoginCentre"));
const LoginAdmin = lazy(() => import("./components/LoginAdmin"));
const PatientLogin = lazy(() => import("./components/PatientLogin"));

/* ---------------- Centre Dashboard ---------------- */
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const DashboardProfilePage = lazy(() => import("./pages/DashboardProfilePage"));
const DoctorsPage = lazy(() => import("./pages/DoctorsPage"));
const EditDoctorPage = lazy(() => import("./pages/EditDoctorPage"));
const PackagePage = lazy(() => import("./pages/PackagePage"));
const FollowerPage = lazy(() => import("./pages/FollowerPage"));
const StatementPage = lazy(() => import("./pages/StatementPage"));
const EarningPage = lazy(() => import("./pages/EarningPage"));
const RefundPage = lazy(() => import("./pages/RefundPage"));
const CreateDoctors = lazy(() => import("./pages/CreateDoctors"));
const CreateService = lazy(() => import("./pages/CreateService"));
const CreatePackage = lazy(() => import("./pages/CreatePackage"));
const EditService = lazy(() => import("./pages/EditService"));
const EditPackage = lazy(() => import("./pages/EditPackage"));
const HelpSupportCentrePage = lazy(
  () => import("./pages/AdminDashboard/Centers/HelpSupportCentrePage"),
);
const UploadReportPage = lazy(
  () => import("./pages/AdminDashboard/Centers/UploadReportPage"),
);
const UserMedicalhistory = lazy(
  () => import("./pages/AdminDashboard/Centers/UserMedicalhistory"),
);

/* ---------------- User Dashboard ---------------- */
const UserDashboardPage = lazy(
  () => import("./pages/UserDashboard/UserDashboardPage"),
);
const UserProfilePage = lazy(
  () => import("./pages/UserDashboard/UserProfilePage"),
);
const MedicalHistoryPage = lazy(
  () => import("./pages/UserDashboard/MedicalHistoryPage"),
);
const ReportsPage = lazy(
  () => import("./pages/AdminDashboard/Users/ReportsPage"),
);
const AddInsurancePage = lazy(
  () => import("./pages/UserDashboard/AddInsurancePage"),
);
const AppointmentsPage = lazy(
  () => import("./pages/UserDashboard/AppointmentsPage"),
);
const RequestsPage = lazy(
  () => import("./pages/UserDashboard/ProductReqPage"),
);
const HelpSupportUserPage = lazy(
  () => import("./pages/UserDashboard/HelpSupportUserPage"),
);
const UserOrdersPage = lazy(
  () => import("./pages/UserDashboard/UserOrdersPage"),
);

/* ---------------- Admin Dashboard ---------------- */
const AdminDashboardPage = lazy(
  () => import("./pages/AdminDashboard/AdminDashboardPage"),
);
const AdminProductManagerPage = lazy(
  () => import("./pages/AdminDashboard/AdminProductManagerPage"),
);
const AdminEarning = lazy(() => import("./pages/AdminDashboard/AdminEarning"));
const AdminProfilePage = lazy(
  () => import("./pages/AdminDashboard/AdminProfilePage"),
);
const HelpAndSupport = lazy(
  () => import("./pages/AdminDashboard/HelpAndSupport"),
);
const RegisteredCentrePage = lazy(
  () => import("./pages/AdminDashboard/Centers/RegisteredCentrePage"),
);
const RegisteredCenters = lazy(
  () => import("./pages/AdminDashboard/Centers/RegisteredCenters"),
);
const ApprovedCenters = lazy(
  () => import("./pages/AdminDashboard/Centers/ApprovedCenters"),
);
const RegisteredServices = lazy(
  () => import("./pages/AdminDashboard/Services/RegisteredServices"),
);
const RegisteredSellers = lazy(
  () => import("./pages/AdminDashboard/Sellers/RegisteredSellers"),
);
const SellerDetailPageForAdmin = lazy(() => import("./pages/AdminDashboard/Sellers/SellerDetailPage"));
const RegisteredSellerPage = lazy(
  () => import("./pages/AdminDashboard/Sellers/RegisteredSellerPage"),
);
const ApprovedSellers = lazy(
  () => import("./pages/AdminDashboard/Sellers/ApprovedSellers"),
);
const BlockedSellers = lazy(
  () => import("./pages/AdminDashboard/Sellers/BlockedSellers"),
);
const CategoryDetailsPage = lazy(
  () => import("./pages/AdminDashboard/Services/CategoryDetailsPage"),
);
const CentreServicesPage = lazy(
  () => import("./pages/AdminDashboard/Centers/CentreServicesPage"),
);
const RegisteredUsers = lazy(
  () => import("./pages/AdminDashboard/Users/RegisteredUsers"),
);
const BookingByCentrePage = lazy(
  () => import("./pages/AdminDashboard/Booking/BookingByCentrePage"),
);
const AdminOrdersPage = lazy(
  () => import("./pages/AdminDashboard/Orders/AdminOrdersPage"),
);
const AdminSellerOrdersDetailPage = lazy(
  () => import("./pages/AdminDashboard/Orders/AdminSellerOrdersDetailPage"),
);
const AdminProductsPage = lazy(
  () => import("./pages/AdminDashboard/Products/AdminProductsPage"),
);
const AdminProductRequestsPage = lazy(
  () => import("./pages/AdminDashboard/ProductRequests/AdminProductRequestsPage"),
);
const UserBookingPage = lazy(() => import("./pages/UserBookingAdmin"));
const UserRequestQuotationsPage = lazy(() => import("./pages/UserRequestQuotationsPage"));
const BookingAdmin = lazy(() => import("./pages/BookingAdmin"));
const BookingSummary = lazy(() => import("./pages/BookingSummary"));
const EarningSummaryByMonthPage = lazy(
  () => import("./pages/EarningSummaryByMonthPage"),
);
const ServicesByCentrePage = lazy(() => import("./pages/ServicesByCentrePage"));
const ShopPage = lazy(() => import("./pages/ShopPage"));
const AllProductsPage = lazy(() => import("./pages/E-Commerce/AllProductsPage"));
const AllSellerPage = lazy(() => import("./pages/E-Commerce/AllSellerPage"));
const AllSellersPage = lazy(() => import("./pages/E-Commerce/AllSellersPage"));
const ProductDetailPage = lazy(() => import("./pages/E-Commerce/ProductDetailPage"));
const MedicineCartPage = lazy(() => import("./pages/E-Commerce/CartPage"));
const MedicineCartAddressPage = lazy(() => import("./pages/E-Commerce/MedicineCartAddressPage"));
const MedicineCartSummaryPage = lazy(() => import("./pages/E-Commerce/MedicineCartSummaryPage"));
const SellerDetailPage = lazy(() => import("./pages/E-Commerce/SellerDetailPage"));
const SellerDashboardPage = lazy(() => import("./pages/E-Commerce/SellerDashboardPage"));
// const DashboardPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/DashboardPage"));
const IncomingRequestsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/IncomingRequestsPage"));
const SubmittedProposalsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/SubmittedProposalsPage"));
const NotParticipatedRequestsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/NotParticipatedRequestsPage"));
const RejectedRequestsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/RejectedRequestsPage"));
const SellerAllProductsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/AllProductsPage"));
const AddProductPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/AddProductPage"));
const InventoryListPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/InventoryListPage"));
const StockAlertsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/StockAlertsPage"));
const ActiveOrdersPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/ActiveOrdersPage"));
const CompletedOrdersPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/CompletedOrdersPage"));
const CancelledOrdersPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/CancelledOrdersPage"));
const PrescriptionRequestsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/PrescriptionRequestsPage"));
const DeliveryZonesPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/DeliveryZonesPage"));
const DeliverySettingsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/DeliverySettingsPage"));
const SellerReportsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/SellerReportsPage"));
const SellerNotificationsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/SellerNotificationsPage"));
const SellerProfilePage = lazy(() => import("./pages/E-Commerce/SellerDashboard/SellerProfilePage"));
const SellerSettingsPage = lazy(() => import("./pages/E-Commerce/SellerDashboard/SellerSettingsPage"));
const SellerDeclarationPage = lazy(() => import("./pages/E-Commerce/SellerDeclarationPage"));


/* ---------------- Protected Route ---------------- */
const ProtectedRoute = ({ children, requiredRole }) => {
  let token;
  let redirectPath = "/shop";

  if (requiredRole === "user") {
    token = localStorage.getItem("userToken") || (localStorage.getItem("role") === "patient" ? localStorage.getItem("token") : null);
  } else if (requiredRole === "centre") {
    token = localStorage.getItem("centreToken") || (localStorage.getItem("role") === "centre" ? localStorage.getItem("token") : null);
    redirectPath = "/";
  } else if (requiredRole === "admin") {
    token = localStorage.getItem("adminToken");
    redirectPath = "/manage4ceps-admin";
  } else if (requiredRole === "seller") {
    token = localStorage.getItem("sellerToken") || (localStorage.getItem("role") === "seller" ? localStorage.getItem("token") : null);
    redirectPath = "/shop";
    try {
      const sellerStr = localStorage.getItem("seller");
      const seller = sellerStr ? JSON.parse(sellerStr) : null;
      if (seller && seller.status === "Blocked") {
        return <Navigate to={redirectPath} replace />;
      }
    } catch (e) { }
  }

  if (!token || isTokenExpired(token)) return <Navigate to={redirectPath} replace />;

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <PortalSelectionModal />
      <AutoLogoutManager />
      <Toaster position="top-right" reverseOrder={false} containerStyle={{ zIndex: 100005 }} />
      <RouteScrollToTop />
      <ScrollToTop
        smooth
        color="#A847F0"
        style={{
          borderRadius: "50%",
          zIndex: 998,
          boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      />
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* <Route exact path="/" element={<Navigate to="/shop" replace />} /> */}
          <Route exact path="/" element={<HomePage />} />
          <Route exact path="/centers" element={<AllCenterPage />} />
          <Route exact path="/login" element={<LoginPage />} />
          <Route exact path="/patient-login" element={<PatientLogin />} />
          <Route exact path="/centre-login" element={<LoginCentre />} />
          <Route exact path="/manage4ceps-admin" element={<LoginAdmin />} />
          <Route exact path="/set-password" element={<SetPassword />} />
          <Route exact path="/forgot-password" element={<ForgotPassword />} />
          <Route exact path="/reset-password" element={<ResetPassword />} />
          <Route exact path="/register" element={<RegisterPage />} />
          <Route exact path="/contact" element={<ContactPage />} />
          <Route exact path="/about-us" element={<AboutUsPage />} />

          <Route
            exact
            path="/centre-register"
            element={<CentreRegisterPage />}
          />
          <Route
            exact
            path="/user-register"
            element={<PatientRegisterPage />}
          />

          <Route exact path="/all-service" element={<ServicesPage />} />

          <Route exact path="/all-package" element={<HealthPackages />} />

          <Route exact path="/user-login" element={<UserLogin />} />
          <Route exact path="/review-login" element={<ReviewLogin />} />

          <Route
            path="/service/:serviceSlug/:id"
            element={<ProductDetailsPage />}
          />
          <Route
            exact
            path="/package/:packageSlug/:id"
            element={<PackageDetsilsPage />}
          />
          <Route exact path="/cart" element={<CartPage />} />
          <Route exact path="/cart-personal" element={<CartPersonalPage />} />
          <Route exact path="/cart-thank-you" element={<CartThankYouPage />} />
          <Route exact path="/terms-booking" element={<TermsBooking />} />
          <Route exact path="/terms" element={<TermsBooking />} />
          <Route exact path="/user-guide" element={<UserGuide />} />
          <Route exact path="/how-it-works" element={<UserGuide />} />
          <Route exact path="/ecommerce-guide" element={<UserGuide />} />
          <Route exact path="/guide" element={<UserGuide />} />
          <Route exact path="/privacy-booking" element={<PrivacyBooking />} />
          <Route exact path="/privacy-policy" element={<PrivacyOrders />} />
          <Route exact path="/privacy" element={<PrivacyBooking />} />
          <Route
            exact
            path="/payment-policy"
            element={<PaymentPolicyCentre />}
          />
          <Route exact path="/all-faqs" element={<AllFaq />} />
          <Route exact path="/pharmacy" element={<PharmacyPage />} />
          <Route
            exact
            path="/medical-instruments"
            element={<MedicalInstrumentsPage />}
          />
          <Route exact path="/health-product" element={<HealthProductPage />} />
          <Route exact path="/shop" element={<ShopPage />} />
          <Route exact path="/all-products" element={<AllProductsPage />} />
          <Route exact path="/all-sellers" element={<AllSellersPage />} />
          <Route exact path="/seller/:sellerSlug" element={<SellerDetailPage />} />



          {/* <Route exact path="/invoice" element={<Invoice />} /> */}

          {/* Centre Dashboard - Protected */}
          <Route
            exact
            path="/centre-dashboard"
            element={
              <ProtectedRoute requiredRole="centre">
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/dashboard-profile"
            element={
              <ProtectedRoute requiredRole="centre">
                <DashboardProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/doctors"
            element={
              <ProtectedRoute requiredRole="centre">
                <DoctorsPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/edit-doctor/:id"
            element={
              <ProtectedRoute requiredRole="centre">
                <EditDoctorPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/package"
            element={
              <ProtectedRoute requiredRole="centre">
                <PackagePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/follower"
            element={
              <ProtectedRoute requiredRole="centre">
                <FollowerPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/statement"
            element={
              <ProtectedRoute requiredRole="centre">
                <StatementPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/earning"
            element={
              <ProtectedRoute requiredRole="centre">
                <EarningPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/refund"
            element={
              <ProtectedRoute requiredRole="centre">
                <RefundPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/create-doctor"
            element={
              <ProtectedRoute requiredRole="centre">
                <CreateDoctors />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/create-service"
            element={
              <ProtectedRoute requiredRole="centre">
                <CreateService />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/create-package"
            element={
              <ProtectedRoute requiredRole="centre">
                <CreatePackage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/edit-service/:id"
            element={
              <ProtectedRoute requiredRole="centre">
                <EditService />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/edit-package/:id"
            element={
              <ProtectedRoute requiredRole="centre">
                <EditPackage />
              </ProtectedRoute>
            }
          />

          {/* User Dashboard - Protected */}
          <Route
            exact
            path="/user-dashboard"
            element={
              <ProtectedRoute requiredRole="user">
                <UserDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/user-profile"
            element={
              <ProtectedRoute requiredRole="user">
                <UserProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/medical-history"
            element={
              <ProtectedRoute requiredRole="user">
                <MedicalHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/booking-report"
            element={
              <ProtectedRoute requiredRole="user">
                <ReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/add-insurance"
            element={
              <ProtectedRoute requiredRole="user">
                <AddInsurancePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/appointments"
            element={
              <ProtectedRoute requiredRole="user">
                <AppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/requests"
            element={
              <ProtectedRoute requiredRole="user">
                <RequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/requests/:id"
            element={
              <ProtectedRoute requiredRole="user">
                <RequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/orders"
            element={
              <ProtectedRoute requiredRole="user">
                <UserOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/user-helpAndSupport"
            element={
              <ProtectedRoute requiredRole="user">
                <HelpSupportUserPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/centre-helpAndSupport"
            element={
              <ProtectedRoute requiredRole="centre">
                <HelpSupportCentrePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/user-medical-history/:id"
            element={
              <ProtectedRoute requiredRole="centre">
                <UserMedicalhistory />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/centre/upload-report"
            element={
              <ProtectedRoute requiredRole="centre">
                <UploadReportPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/centre/:centreSlug/:id"
            element={<ProfilePage />}
          />

          {/* User Dashboard end */}

          {/* Admin Dashboard - Protected */}
          <Route
            exact
            path="/admin-dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/admin-dashboard/product-manager"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminProductManagerPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/adminDashboard-profile"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminDashboard-earning"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminEarning />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/earningSummary"
            element={
              <ProtectedRoute requiredRole="admin">
                <BookingSummary />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/earningSumary/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <EarningSummaryByMonthPage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/adminDashboard-helpAndSupport"
            element={
              <ProtectedRoute requiredRole="admin">
                <HelpAndSupport />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/registered"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredCentrePage />
              </ProtectedRoute>
            }
          />

          <Route
            exact
            path="/center-registered"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredCenters />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/center-approved"
            element={
              <ProtectedRoute requiredRole="admin">
                <ApprovedCenters />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/registered-sellers"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredSellerPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-under-approval"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredSellers />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-under-approval/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <SellerDetailPageForAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-approved"
            element={
              <ProtectedRoute requiredRole="admin">
                <ApprovedSellers />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/blocked-sellers"
            element={
              <ProtectedRoute requiredRole="admin">
                <BlockedSellers />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-blocked"
            element={
              <ProtectedRoute requiredRole="admin">
                <BlockedSellers />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/all-services-list"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredServices />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/admin-products"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminProductsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminBooking"
            element={
              <ProtectedRoute requiredRole="admin">
                <BookingByCentrePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminBooking/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <BookingAdmin />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminOrders"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminOrders/:sellerId"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminSellerOrdersDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/adminProductRequests"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminProductRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/userBooking/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserBookingPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/userBooking/:id/request/:requestId/quotations"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserRequestQuotationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/userBooking/request/:requestId/quotations"
            element={
              <ProtectedRoute requiredRole="admin">
                <UserRequestQuotationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/category-services/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <CategoryDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/centre-services/:id"
            element={
              <ProtectedRoute requiredRole="admin">
                <CentreServicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/services/:id"
            element={<ServicesByCentrePage />}
          />
          <Route
            exact
            path="/users"
            element={
              <ProtectedRoute requiredRole="admin">
                <RegisteredUsers />
              </ProtectedRoute>
            }
          />

          <Route exact path="/seller/:sellerSlug/:id" element={<SellerDetailPage />} />
          <Route exact path="/medicine/cart" element={<MedicineCartPage />} />
          <Route exact path="/medicine/cart/address" element={<MedicineCartAddressPage />} />
          <Route exact path="/medicine/cart/summary" element={<MedicineCartSummaryPage />} />
          <Route exact path="/cart" element={<MedicineCartPage />} />
          <Route exact path="/cart/address" element={<MedicineCartAddressPage />} />
          <Route exact path="/cart/summary" element={<MedicineCartSummaryPage />} />

          {/* Seller Dashboard */}
          <Route
            exact
            path="/seller-dashboard"
            element={<ProtectedRoute requiredRole="seller">
              <SellerDashboardPage />
            </ProtectedRoute>}
          />
          <Route
            exact
            path="/seller-dashboard/incoming-requests"
            element={
              <ProtectedRoute requiredRole="seller">
                <IncomingRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/submitted-proposals"
            element={
              <ProtectedRoute requiredRole="seller">
                <SubmittedProposalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/participated-requests"
            element={
              <ProtectedRoute requiredRole="seller">
                <SubmittedProposalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/not-participated-requests"
            element={
              <ProtectedRoute requiredRole="seller">
                <NotParticipatedRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/rejected-requests"
            element={<ProtectedRoute requiredRole="seller">
              <RejectedRequestsPage />
            </ProtectedRoute>}
          />
          {/* <Route
            exact
            path="/seller-dashboard/all-products"
            element={<SellerAllProductsPage />}
          /> */}
          {/* <Route
            exact
            path="/seller-dashboard/add-product"
            element={<AddProductPage />}
          /> */}
          {/* <Route
            exact
            path="/seller-dashboard/inventory-list"
            element={<InventoryListPage />}
          /> */}
          <Route
            exact
            path="/seller-dashboard/stock-alerts"
            element={
              <ProtectedRoute requiredRole="seller">
                <StockAlertsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/active-orders"
            element={
              <ProtectedRoute requiredRole="seller">
                <ActiveOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/completed-orders"
            element={
              <ProtectedRoute requiredRole="seller">
                <CompletedOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/cancelled-orders"
            element={
              <ProtectedRoute requiredRole="seller">
                <CancelledOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/reports"
            element={
              <ProtectedRoute requiredRole="seller">
                <SellerReportsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/notifications"
            element={
              <ProtectedRoute requiredRole="seller">
                <SellerNotificationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/profile"
            element={
              <ProtectedRoute requiredRole="seller">
                <SellerProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/settings"
            element={
              <ProtectedRoute requiredRole="seller">
                <SellerSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/seller-dashboard/activities"
            element={
              <ProtectedRoute requiredRole="seller">
                <SellerDashboardPage activeTab="activities" />
              </ProtectedRoute>
            }
          />
          <Route exact path="/seller/declaration" element={<SellerDeclarationPage />} />
          <Route exact path="/seller-declaration" element={<SellerDeclarationPage />} />
          <Route exact path="/declaration" element={<SellerDeclarationPage />} />

          <Route exact path="/:category/:id" element={<ProductDetailPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
    // </CartProvider>
  );
}

export default App;
